/*----------------------------------
- COMPONENT: HOME DOMAINS PREVIEW
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @rule Home carries the `/domains` row contract, capped at ten rows from the same bare search, in
 *       engine order: no sort control, no pagination, and no fallback rows.
 *
 * The real bounded preview under the hero, replacing the screener film on 2026-09-27: the film was
 * the phone LCP element and showed made-up rows, while the real table is the surface that converts.
 * It renders through the listing's own block, so a Home row and a `/domains` row can never read
 * differently.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import PublicDomainsPreviewBlock from '@/client/pages/landing/domains/page/components/PublicDomainsPreviewBlock';
import { formatPositiveCountStat } from '@/client/pages/landing/lib/formatCompactStat';
import useHomeDomainsPreviewHandoff, {
    HOME_DOMAINS_PREVIEW_PAGE,
    HOME_DOMAINS_PREVIEW_SECTION,
} from '@/client/pages/landing/lib/useHomeDomainsPreviewHandoff';

// Types
import type { HomeDomainsPreviewData } from '@/client/pages/landing/lib/homeDomainsPreview';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeDomainsPreviewProps = {
    preview: HomeDomainsPreviewData;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeDomainsPreview = ({ preview }: HomeDomainsPreviewProps) => {
    const { search, rows, projection } = preview;
    const { handleSeeAll, handleUnlockRow, handleAddRow } = useHomeDomainsPreviewHandoff({ search });

    return (
        /* No heading: the table follows the hero directly (Gaetan, 2026-09-27). The veil is half the
           listing's height here, `ud-home-domains-preview` in publicDomainsTable.css. */
        <div class="ud-home-domains-preview min-w-0" data-testid="home-domains-preview">
            <PublicDomainsPreviewBlock
                rows={rows}
                projection={projection}
                totalCountLabel={formatPositiveCountStat(search.stats?.count, search.stats?.countPrecision)}
                countPrecision={search.stats?.countPrecision ?? 'exact'}
                page={HOME_DOMAINS_PREVIEW_PAGE}
                section={HOME_DOMAINS_PREVIEW_SECTION}
                triggerId="website_landing_row_preview"
                onSeeAll={handleSeeAll}
                onUnlockRow={handleUnlockRow}
                onAddRow={handleAddRow}
            />
        </div>
    );
};

export default HomeDomainsPreview;
